import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coffee, LogOut, RefreshCw, Truck, UtensilsCrossed, Home, ShieldCheck } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { BrandFooter } from '@/components/BrandFooter';
import { NotificationsToggle } from '@/components/NotificationsToggle';
import { useAuthStore } from '@/stores/authStore';
import { syncService } from '@/services/sync';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';

export function MainMenuPage() {
  const navigate = useNavigate();
  const driver = useAuthStore((s) => s.driver);
  const logout = useAuthStore((s) => s.logout);
  const [confirmLogout, setConfirmLogout] = useState(false);

  // Counts are queried imperatively rather than via a live IndexedDB
  // subscription: iOS can silently drop live-query subscriptions across an
  // app suspend, leaving the badges frozen at 0 while the Deliveries page
  // (freshly mounted) shows the real list. Re-query on mount, after every
  // sync cycle, and whenever the app returns to the foreground.
  const [counts, setCounts] = useState<{ load: number; unload: number } | null>(null);
  const [syncing, setSyncing] = useState(false);

  const refreshCounts = useCallback(async () => {
    const [load, unload] = await Promise.all([
      assignmentRepo.getLoadItems(),
      assignmentRepo.getUnloadItems(),
    ]);
    setCounts({ load: load.length, unload: unload.length });
  }, []);

  useEffect(() => {
    syncService.setContext('mainMenu');
    void syncService.runOnce('manual');
    void refreshCounts();

    const unsubscribe = syncService.subscribe((e) => {
      if (e.status === 'started') setSyncing(true);
      else {
        setSyncing(false);
        void refreshCounts();
      }
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshCounts();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshCounts]);

  // Never present 0 as authoritative while dispatch is still being checked
  // (fresh open, or launched from a push notification before the sync lands).
  const badge = (n: number | undefined) =>
    n === undefined || (syncing && n === 0) ? '…' : String(n);
  const loadCount = badge(counts?.load);
  const unloadCount = badge(counts?.unload);

  const driverLabel = useMemo(() => driver?.driverNumber ?? 'driver', [driver]);

  return (
    <AppShell
      title={`Welcome, ${driverLabel}`}
      rightSlot={
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pod-recovery')}
            aria-label="POD recovery"
          >
            <ShieldCheck className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirmLogout(true)} aria-label="Sign out">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Card
          accent="pickup"
          className="cursor-pointer hover:bg-brand-surfaceAlt"
          onClick={() => navigate('/load')}
          aria-label="Look up new pickup"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm uppercase text-brand-muted">Pickups</div>
              <div className="text-3xl font-extrabold text-brand-pickup">{loadCount}</div>
            </div>
            <Truck className="h-10 w-10 text-brand-pickup" />
          </div>
        </Card>

        <Card
          accent="delivery"
          className="cursor-pointer hover:bg-brand-surfaceAlt"
          onClick={() => navigate('/unload')}
          aria-label="Deliveries"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm uppercase text-brand-muted">Deliveries</div>
              <div className="text-3xl font-extrabold text-brand-delivery">{unloadCount}</div>
            </div>
            <Truck className="h-10 w-10 text-brand-delivery" />
          </div>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Button variant="meal" size="lg" onClick={() => navigate('/status/meal')}>
          <UtensilsCrossed className="h-5 w-5" />
          Meal
        </Button>
        <Button variant="coffee" size="lg" onClick={() => navigate('/status/coffee')}>
          <Coffee className="h-5 w-5" />
          Break
        </Button>
        <Button variant="primary" size="lg" onClick={() => void syncService.runOnce('manual')}>
          <RefreshCw className="h-5 w-5" />
          Update data
        </Button>
        <Button variant="back" size="lg" onClick={() => navigate('/status/base')}>
          <Home className="h-5 w-5" />
          Back to terminal
        </Button>
      </div>

      <NotificationsToggle />

      <BrandFooter className="mt-8" />

      <Dialog open={confirmLogout} onClose={() => setConfirmLogout(false)} title="Logout">
        <p className="text-brand-muted">Are you sure you want to log out?</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmLogout(false)}>
            Cancel
          </Button>
          <Button
            variant="ghost"
            className="text-brand-error"
            onClick={() => {
              setConfirmLogout(false);
              void logout();
            }}
          >
            Logout
          </Button>
        </div>
      </Dialog>
    </AppShell>
  );
}
